import React from "react";

const nearbyAttractions = [
  {
    name: "K2 Base Camp Trek",
    distance: "~3 days trek",
    desc: "The crown jewel of Karakoram trekking. Our guides can arrange full logistics.",
    icon: "🏔️",
    badgeClass: "badge-ocean",
    badge: "Trekking",
  },
  {
    name: "Deosai National Park",
    distance: "2 hrs by jeep",
    desc: "One of the world's highest plateaus — wildflowers, brown bears, and zero crowds.",
    icon: "🌿",
    badgeClass: "badge-teal",
    badge: "Nature",
  },
  {
    name: "Shangrila Lake",
    distance: "25 min drive",
    desc: "The legendary 'Heaven on Earth' lake surrounded by weeping willows.",
    icon: "🌊",
    badgeClass: "badge-ocean",
    badge: "Scenic",
  },
  {
    name: "Katpana Cold Desert",
    distance: "15 min drive",
    desc: "World's highest cold desert — rolling sand dunes set against icy peaks.",
    icon: "🏜️",
    badgeClass: "badge-sand",
    badge: "Unique",
  },
  {
    name: "Skardu Bazaar",
    distance: "10 min walk",
    desc: "Local crafts, dried apricots, fresh bread, and the heartbeat of Baltistan.",
    icon: "🛍️",
    badgeClass: "badge-sand",
    badge: "Culture",
  },
  {
    name: "Kachura Lakes",
    distance: "30 min drive",
    desc: "Twin alpine lakes nestled between pine forests and snow-capped ridges.",
    icon: "💎",
    badgeClass: "badge-teal",
    badge: "Scenic",
  },
];

export default function Location() {
  return (
    <section className="section-pad bg-section-light">
      <div className="container-base">
        {/* Header */}
        <div className="text-center mb-14">
          <p className="section-subtitle mb-3">Location</p>
          <h2 className="section-title mb-4">Where We Are</h2>
          <p className="section-lead">
            Perched on a panoramic hillside above Skardu city, Mariner Lodge puts
            you at the gateway to the world's greatest mountain adventures.
          </p>
        </div>

        {/* Map + address */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 mb-16 items-start">
          {/* Map */}
          <div className="lg:col-span-3 rounded-2xl overflow-hidden shadow-ocean border border-ocean-100">
            <iframe
              title="Mariner Lodge on Map"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d52791.6!2d75.55!3d35.29!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x38e4617d6a41b437%3A0x2ef7d6e71f5e7aca!2sSkardu%2C%20Pakistan!5e0!3m2!1sen!2s!4v1700000000000"
              width="100%"
              height="360"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="block"
            />
          </div>

          {/* Getting here */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            <div>
              <h3 className="font-serif text-2xl font-bold text-slate-900 mb-4">
                Getting Here
              </h3>
              <ul className="space-y-4">
                {[
                  {
                    icon: "✈️",
                    title: "By Air",
                    desc: "Skardu Airport (SKT) — direct flights from Islamabad. 55 min flight. We offer complimentary airport pickup.",
                  },
                  {
                    icon: "🚗",
                    title: "By Road",
                    desc: "Karakoram Highway from Islamabad — a legendary 18–20 hr scenic drive through Gilgit-Baltistan.",
                  },
                  {
                    icon: "📍",
                    title: "Address",
                    desc: "Panoramic Road, Skardu, Gilgit-Baltistan 16100, Pakistan.",
                  },
                ].map((item) => (
                  <li key={item.title} className="flex gap-3 items-start">
                    <span className="text-2xl flex-shrink-0">{item.icon}</span>
                    <div>
                      <p className="font-semibold text-slate-800 text-body-sm">{item.title}</p>
                      <p className="text-slate-500 text-body-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <a
              href="https://maps.google.com/?q=Skardu,Pakistan"
              target="_blank"
              rel="noreferrer"
              className="btn-secondary self-start"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Open in Google Maps
            </a>
          </div>
        </div>

        {/* Nearby attractions */}
        <div>
          <div className="text-center mb-10">
            <p className="section-subtitle mb-2">Explore</p>
            <h3 className="font-serif text-2xl md:text-3xl font-bold text-slate-900">
              Nearby Attractions
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {nearbyAttractions.map((attr, i) => (
              <div
                key={i}
                className="card p-6 flex gap-4 items-start hover:-translate-y-1 transition-transform duration-300"
              >
                <span className="text-3xl flex-shrink-0" aria-hidden="true">
                  {attr.icon}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h4 className="font-semibold text-slate-900 text-body-sm">
                      {attr.name}
                    </h4>
                    <span className={`${attr.badgeClass} badge`}>{attr.badge}</span>
                  </div>
                  <p className="text-body-xs text-ocean-600 font-medium mb-1">
                    {attr.distance}
                  </p>
                  <p className="text-slate-500 text-body-xs leading-relaxed">
                    {attr.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}